import { ref } from 'vue'
import { useStoreOperation } from '@/core/composables/useStoreOperation'
import { logger } from '@/core/utils/logger'

export interface EntityStoreOptions<T, CreateDTO = unknown, UpdateDTO = unknown> {
  entityName: string
  fetchEntities: () => Promise<T[]>
  createEntity?: (data: CreateDTO) => Promise<T>
  updateEntity?: (id: string, data: UpdateDTO) => Promise<T>
  deleteEntity?: (id: string) => Promise<void>
}

/**
 * Generic composable for creating a store with CRUD operations
 * Handles common patterns: loading, error, dataFetched and basic CRUD operations
 * Used for global (non-project-scoped) entities like Projects and Users
 */
export function useEntityStore<T extends { id: string }, CreateDTO = unknown, UpdateDTO = unknown>(
  options: EntityStoreOptions<T, CreateDTO, UpdateDTO>
) {
  const {
    entityName,
    fetchEntities,
    createEntity,
    updateEntity,
    deleteEntity
  } = options

  const items = ref<T[]>([])
  const selectedEntity = ref<T | null>(null)
  const loading = ref(false)
  const error = ref<unknown>(null)
  const dataFetched = ref(false)

  // Use centralized store operation helper
  const { execute, executeSilent } = useStoreOperation({ loading, error })

  // Formát zpráv pro success a error
  const entityNameLower = entityName.toLowerCase()
  const messages = {
    fetchError: `Načtení ${entityNameLower}ů selhalo`,
    createSuccess: `${entityName} byl úspěšně vytvořen`,
    createError: `Vytvoření ${entityNameLower}u selhalo`,
    updateSuccess: `${entityName} byl úspěšně aktualizován`,
    updateError: `Aktualizace ${entityNameLower}u selhala`,
    deleteSuccess: `${entityName} byl úspěšně smazán`,
    deleteError: `Smazání ${entityNameLower}u selhalo`
  }

  // Fetch all entities (no success notification for reads)
  async function fetch(forceReload = false) {
    if (dataFetched.value && !forceReload) {
      return items.value
    }

    const result = await executeSilent(async () => {
      const data = await fetchEntities()
      items.value = data
      dataFetched.value = true
      return items.value
    })

    if (result === null && error.value) {
      throw error.value
    }

    return result
  }

  // Fetch single entity (no success notification for reads)
  async function fetchOne(id: string) {
    const result = await executeSilent(async () => {
      // Ensure items are loaded
      await fetch()

      // Find item in loaded items
      const item = items.value.find(e => e.id === id)
      if (!item) {
        throw new Error(`${entityName} s ID ${id} nebyl nalezen`)
      }

      selectedEntity.value = item
      return item
    })

    if (result === null && error.value) {
      throw error.value
    }

    return result
  }

  // Create new entity
  async function create(data: CreateDTO) {
    if (!createEntity) {
      logger.warn(`Create operation not implemented for ${entityName}`)
      return null
    }

    const result = await execute(
      async () => {
        const entity = await createEntity(data)
        items.value = [...items.value, entity] as T[]
        return entity
      },
      { successMessage: messages.createSuccess }
    )

    if (result === null && error.value) {
      throw error.value
    }

    return result
  }

  // Update existing entity
  async function update(id: string, data: UpdateDTO) {
    if (!updateEntity) {
      logger.warn(`Update operation not implemented for ${entityName}`)
      return null
    }

    const result = await execute(
      async () => {
        const entity = await updateEntity(id, data)
        const index = items.value.findIndex(e => e.id === id)
        if (index !== -1) {
          items.value = items.value.map((e, i) => i === index ? entity : e) as T[]
        }
        return entity
      },
      { successMessage: messages.updateSuccess }
    )

    if (result === null && error.value) {
      throw error.value
    }

    return result
  }

  // Delete entity
  async function deleteItem(id: string) {
    if (!deleteEntity) {
      logger.warn(`Delete operation not implemented for ${entityName}`)
      return
    }

    const result = await execute(
      async () => {
        await deleteEntity(id)
        items.value = items.value.filter(e => e.id !== id)
        return true
      },
      { successMessage: messages.deleteSuccess }
    )

    if (result === null && error.value) {
      throw error.value
    }

    return result
  }

  function resetFetchState() {
    dataFetched.value = false
  }

  function selectEntity(id: string | null) {
    if (id === null) {
      selectedEntity.value = null
      return
    }

    selectedEntity.value = items.value.find(e => e.id === id) || null
  }

  return {
    items,
    selectedEntity,
    loading,
    error,
    dataFetched,
    fetch,
    fetchOne,
    create,
    update,
    delete: deleteItem,
    resetFetchState,
    selectEntity
  }
}